import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../config';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonText,
  IonIcon,
  IonSpinner,
  IonGrid,
  IonRow,
  IonCol,
  IonSegment,
  IonSegmentButton
} from '@ionic/react';
import { 
  arrowBackOutline, 
  calculatorOutline,
  heartOutline,
  warningOutline,
  informationCircleOutline
} from 'ionicons/icons';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './PreventCalculator.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const PreventCalculator = ({ member_id, auth_token, onBack }) => {
  const history = useHistory();
  
  // State for input parameters
  const [inputs, setInputs] = useState({
    sex: '',
    age: '',
    tc: '',
    hdl: '',
    sbp: '',
    dm: '',
    smoking: '',
    bmi: '',
    egfr: '',
    bptreat: '',
    statin: '',
    uacr: '',
    hba1c: '',
    height: ''
  });

  // State for results and UI
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState('inputs');
  const [loadingMemberData, setLoadingMemberData] = useState(false);
  const [dataSourceDate, setDataSourceDate] = useState(null);
  
  // State for simulations
  const [simulations, setSimulations] = useState([]);
  const [selectedSimulation, setSelectedSimulation] = useState('baseline');
  const [riskPeriod, setRiskPeriod] = useState('10'); // '10' for 10-year, '30' for 30-year
  const [riskType, setRiskType] = useState('cvd'); // 'cvd' or 'ascvd'
  
  // State for AI optimization
  const [optimizationError, setOptimizationError] = useState(null);

  // Message-event based authentication handling (iframe mode)
  const [effectiveMemberId, setEffectiveMemberId] = useState(() => member_id || localStorage.getItem('member_id') || '');
  const [effectiveAuthToken, setEffectiveAuthToken] = useState(() => auth_token || localStorage.getItem('auth_token') || '');
  useEffect(() => {
    const allowedOrigins = [
      'https://www.infinity-mc-hms.com',
      'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
      'http://localhost:3000',
      'http://localhost:5000',
      'https://localhost:3000',
      'https://localhost:5000'
    ];
    
    const handleMessage = (event) => {
      // Ignore MetaMask, webpack, etc. messages
      if (event.data?.target === 'metamask-contentscript' || 
          event.data?.target === 'metamask-inpage' ||
          event.data?.type === 'webpackWarnings') {
        return;
      }
      if (!allowedOrigins.includes(event.origin)) return;
      if (event.data && event.data.type === 'AUTH_DATA') {
        const { member_id: receivedMemberId, auth_token: receivedAuthToken } = event.data;
        if (receivedMemberId && receivedAuthToken) {
          localStorage.setItem('member_id', receivedMemberId);
          localStorage.setItem('auth_token', receivedAuthToken);
          setEffectiveMemberId(receivedMemberId);
          setEffectiveAuthToken(receivedAuthToken);
          console.log('Auth data received:', { 
            member_id: receivedMemberId, 
            auth_token: receivedAuthToken ? '***' : undefined
          });
        } else {
          // Optionally handle error state here if desired
        }
      }
    };

    window.addEventListener('message', handleMessage);
    // If not received, request auth from parent
    if (!effectiveMemberId || !effectiveAuthToken) {
      allowedOrigins.forEach(origin => {
        try {
          window.parent.postMessage({ type: 'REQUEST_AUTH' }, origin);
        } catch (error) {}
      });
    }
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [effectiveMemberId, effectiveAuthToken]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate inputs
  const validateInputs = () => {
    const errors = {};
    const requiredFields = ['sex', 'age', 'tc', 'hdl', 'sbp', 'dm', 'smoking', 'bmi', 'egfr', 'bptreat', 'statin'];
    
    // Check required fields
    requiredFields.forEach(field => {
      const value = inputs[field];
      if (!value || value.trim() === '') {
        errors[field] = `${getFieldLabel(field)} is required`;
      } else if (isNaN(Number(value))) {
        errors[field] = `${getFieldLabel(field)} must be a number`;
      }
    });

    // Check optional fields if provided
    const optionalFields = ['uacr', 'hba1c', 'height'];
    optionalFields.forEach(field => {
      const value = inputs[field];
      if (value && value.trim() !== '' && isNaN(Number(value))) {
        errors[field] = `${getFieldLabel(field)} must be a number`;
      }
    });

    // Validate Total Cholesterol range (3.4-8.3 mmol/L)
    if (inputs.tc && !isNaN(Number(inputs.tc))) {
      const tcValue = Number(inputs.tc);
      if (tcValue < 3.4 || tcValue > 8.3) {
        errors.tc = 'Total Cholesterol must be between 3.4-8.3 mmol/L';
      }
    }

    // Validate HDL Cholesterol range (0.5-2.6 mmol/L)
    if (inputs.hdl && !isNaN(Number(inputs.hdl))) {
      const hdlValue = Number(inputs.hdl);
      if (hdlValue < 0.5 || hdlValue > 2.6) {
        errors.hdl = 'HDL Cholesterol must be between 0.5-2.6 mmol/L';
      }
    }

    // Specific validations
    if (inputs.sex && (inputs.sex !== '0' && inputs.sex !== '1')) {
      errors.sex = 'Sex must be 0 (male) or 1 (female)';
    }
    
    if (inputs.age && (Number(inputs.age) < 30 || Number(inputs.age) > 79)) {
      errors.age = 'Age must be between 30-79 years';
    }
    
    if (inputs.dm && (inputs.dm !== '0' && inputs.dm !== '1')) {
      errors.dm = 'Diabetes status must be 0 (no) or 1 (yes)';
    }
    
    if (inputs.smoking && (inputs.smoking !== '0' && inputs.smoking !== '1')) {
      errors.smoking = 'Smoking status must be 0 (no) or 1 (yes)';
    }
    
    if (inputs.bptreat && (inputs.bptreat !== '0' && inputs.bptreat !== '1')) {
      errors.bptreat = 'BP treatment must be 0 (no) or 1 (yes)';
    }
    
    if (inputs.statin && (inputs.statin !== '0' && inputs.statin !== '1')) {
      errors.statin = 'Statin use must be 0 (no) or 1 (yes)';
    }

    // Systolic Blood Pressure validation
    if (inputs.sbp) {
      const sbpValue = Number(inputs.sbp);
      if (isNaN(sbpValue)) {
        errors.sbp = 'Systolic Blood Pressure must be a number';
      } else if (sbpValue < 90 || sbpValue > 200) {
        errors.sbp = 'Systolic Blood Pressure must be between 90-200 mmHg';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Get field label for display
  const getFieldLabel = (field) => {
    const labels = {
      sex: 'Sex',
      age: 'Age',
      tc: 'Total Cholesterol (mmol/L)',
      hdl: 'HDL Cholesterol (mmol/L)',
      sbp: 'Systolic Blood Pressure',
      dm: 'Diabetes Status',
      smoking: 'Smoking Status',
      bmi: 'BMI',
      egfr: 'eGFR',
      bptreat: 'BP Treatment',
      statin: 'Statin Use',
      uacr: 'UACR',
      hba1c: 'HbA1c',
      height: 'Height (cm)'
    };
    return labels[field] || field;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateInputs()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setLoading(true);
    setError(null);
    setOptimizationError(null);
    setResults(null);
    setSimulations([]);
    setSelectedSimulation('baseline');
    setRiskPeriod('10');

    try {
      // Store base inputs for comparison
      const baseInputs = { ...inputs };
      
      // Run baseline calculation
      const baselineResult = await runSimulation(baseInputs, 'baseline');
      setResults(baselineResult);
      
      const allSimulations = [
        { name: 'baseline', label: 'Baseline (Current)', result: baselineResult, inputs: baseInputs, changes: {}, weight: null, notes: [] }
      ];

      // Call optimization endpoint with baseline inputs
      try {
        const optimizationResponse = await callOptimizationEndpoint(baseInputs);
        
        // Convert optimized inputs back to UI format
        const optimizedInputs = { ...baseInputs };
        
        // Convert cholesterol from mg/dL to mmol/L
        if (optimizationResponse.tc !== undefined) {
          optimizedInputs.tc = convertCholesterolToMmolL(optimizationResponse.tc.toString());
        }
        if (optimizationResponse.hdl !== undefined) {
          optimizedInputs.hdl = convertCholesterolToMmolL(optimizationResponse.hdl.toString());
        }
        
        // Convert UACR from mg/g to mg/mmol
        if (optimizationResponse.uacr !== undefined && optimizationResponse.uacr !== null) {
          optimizedInputs.uacr = convertUACRToMgMmol(optimizationResponse.uacr.toString());
        }
        
        // Update other fields from optimization response
        if (optimizationResponse.age !== undefined && optimizationResponse.age !== null) {
          optimizedInputs.age = optimizationResponse.age.toString();
        }
        if (optimizationResponse.bmi !== undefined && optimizationResponse.bmi !== null) {
          optimizedInputs.bmi = optimizationResponse.bmi.toString();
        }
        if (optimizationResponse.sbp !== undefined && optimizationResponse.sbp !== null) {
          optimizedInputs.sbp = optimizationResponse.sbp.toString();
        }
        if (optimizationResponse.egfr !== undefined && optimizationResponse.egfr !== null) {
          optimizedInputs.egfr = optimizationResponse.egfr.toString();
        }
        if (optimizationResponse.hba1c !== undefined && optimizationResponse.hba1c !== null) {
          optimizedInputs.hba1c = optimizationResponse.hba1c.toString();
        }
        if (optimizationResponse.sex !== undefined) {
          optimizedInputs.sex = optimizationResponse.sex.toString();
        }
        if (optimizationResponse.dm !== undefined) {
          optimizedInputs.dm = optimizationResponse.dm.toString();
        }
        if (optimizationResponse.smoking !== undefined) {
          optimizedInputs.smoking = optimizationResponse.smoking.toString();
        }
        if (optimizationResponse.bptreat !== undefined) {
          optimizedInputs.bptreat = optimizationResponse.bptreat.toString();
        }
        if (optimizationResponse.statin !== undefined) {
          optimizedInputs.statin = optimizationResponse.statin.toString();
        }

        // Run optimized inputs through prevent endpoint
        const aiOptimalResult = await runSimulation(optimizedInputs, 'AI Optimal');
        const aiOptimalChanges = trackChanges(baseInputs, optimizedInputs);
        
        // Calculate weight if height and BMI are available
        let aiOptimalWeight = null;
        if (optimizedInputs.height && optimizedInputs.bmi) {
          const weight = calculateWeightFromBMI(optimizedInputs.height, optimizedInputs.bmi);
          aiOptimalWeight = weight ? weight.toFixed(2) : null;
        }

        // Add AI Optimal simulation after baseline
        allSimulations.push({
          name: 'ai_optimal',
          label: 'AI Optimal',
          result: aiOptimalResult,
          inputs: optimizedInputs,
          changes: aiOptimalChanges,
          weight: aiOptimalWeight,
          notes: [],
          explanation: optimizationResponse.explanation || ''
        });
      } catch (optErr) {
        console.error('Error in AI optimization:', optErr);
        setOptimizationError('AI optimization failed, but other simulations will continue.');
        // Continue with other simulations even if optimization fails
      }

      // Store current weight if height and BMI are available
      let currentWeight = null;
      if (inputs.height && inputs.bmi) {
        currentWeight = calculateWeightFromBMI(inputs.height, inputs.bmi);
      }

      // Simulation 1: 5% weight loss
      if (inputs.height && inputs.bmi) {
        try {
          const weight = calculateWeightFromBMI(inputs.height, inputs.bmi);
          if (weight) {
            const newWeight = weight * 0.95; // 5% reduction
            const newBMI = recalculateBMIFromWeight(newWeight, inputs.height);
            const modifiedInputs = { ...baseInputs, bmi: newBMI };
            const result = await runSimulation(modifiedInputs, 'Weight Loss 5%');
            const changes = trackChanges(baseInputs, modifiedInputs);
            allSimulations.push({ 
              name: 'weight_loss_5', 
              label: '5% Weight Loss', 
              result, 
              inputs: modifiedInputs, 
              changes,
              weight: newWeight.toFixed(2),
              notes: []
            });
          }
        } catch (err) {
          console.error('Error in weight loss 5% simulation:', err);
        }
      }

      // Simulation 2: 10% weight loss
      if (inputs.height && inputs.bmi) {
        try {
          const weight = calculateWeightFromBMI(inputs.height, inputs.bmi);
          if (weight) {
            const newWeight = weight * 0.90; // 10% reduction
            const newBMI = recalculateBMIFromWeight(newWeight, inputs.height);
            const modifiedInputs = { ...baseInputs, bmi: newBMI };
            const result = await runSimulation(modifiedInputs, 'Weight Loss 10%');
            const changes = trackChanges(baseInputs, modifiedInputs);
            allSimulations.push({ 
              name: 'weight_loss_10', 
              label: '10% Weight Loss', 
              result, 
              inputs: modifiedInputs, 
              changes,
              weight: newWeight.toFixed(2),
              notes: []
            });
          }
        } catch (err) {
          console.error('Error in weight loss 10% simulation:', err);
        }
      }

      // Simulation 3: 5% Total Cholesterol reduction
      if (inputs.tc) {
        try {
          const tcValue = parseFloat(inputs.tc);
          const newTC = (tcValue * 0.95).toFixed(2);
          const modifiedInputs = { ...baseInputs, tc: newTC };
          const result = await runSimulation(modifiedInputs, 'TC Reduction 5%');
          const changes = trackChanges(baseInputs, modifiedInputs);
          allSimulations.push({ 
            name: 'tc_reduction_5', 
            label: '5% Total Cholesterol Reduction', 
            result, 
            inputs: modifiedInputs, 
            changes,
            weight: currentWeight ? currentWeight.toFixed(2) : null,
            notes: []
          });
        } catch (err) {
          console.error('Error in TC reduction 5% simulation:', err);
        }
      }

      // Simulation 4: 10% Total Cholesterol reduction
      if (inputs.tc) {
        try {
          const tcValue = parseFloat(inputs.tc);
          const newTC = (tcValue * 0.90).toFixed(2);
          const modifiedInputs = { ...baseInputs, tc: newTC };
          const result = await runSimulation(modifiedInputs, 'TC Reduction 10%');
          const changes = trackChanges(baseInputs, modifiedInputs);
          allSimulations.push({ 
            name: 'tc_reduction_10', 
            label: '10% Total Cholesterol Reduction', 
            result, 
            inputs: modifiedInputs, 
            changes,
            weight: currentWeight ? currentWeight.toFixed(2) : null,
            notes: []
          });
        } catch (err) {
          console.error('Error in TC reduction 10% simulation:', err);
        }
      }

      // Simulation 5: 5% Systolic BP reduction
      if (inputs.sbp) {
        try {
          const sbpValue = parseFloat(inputs.sbp);
          const calculatedSBP = Math.round(sbpValue * 0.95);
          const clampedSBP = clampSBP(calculatedSBP);
          const modifiedInputs = { ...baseInputs, sbp: clampedSBP.value.toString() };
          const result = await runSimulation(modifiedInputs, 'SBP Reduction 5%');
          const changes = trackChanges(baseInputs, modifiedInputs);
          const notes = clampedSBP.note ? [clampedSBP.note] : [];
          allSimulations.push({ 
            name: 'sbp_reduction_5', 
            label: '5% Systolic BP Reduction', 
            result, 
            inputs: modifiedInputs, 
            changes,
            weight: currentWeight ? currentWeight.toFixed(2) : null,
            notes
          });
        } catch (err) {
          console.error('Error in SBP reduction 5% simulation:', err);
        }
      }

      // Simulation 6: 10% Systolic BP reduction
      if (inputs.sbp) {
        try {
          const sbpValue = parseFloat(inputs.sbp);
          const calculatedSBP = Math.round(sbpValue * 0.90);
          const clampedSBP = clampSBP(calculatedSBP);
          const modifiedInputs = { ...baseInputs, sbp: clampedSBP.value.toString() };
          const result = await runSimulation(modifiedInputs, 'SBP Reduction 10%');
          const changes = trackChanges(baseInputs, modifiedInputs);
          const notes = clampedSBP.note ? [clampedSBP.note] : [];
          allSimulations.push({ 
            name: 'sbp_reduction_10', 
            label: '10% Systolic BP Reduction', 
            result, 
            inputs: modifiedInputs, 
            changes,
            weight: currentWeight ? currentWeight.toFixed(2) : null,
            notes
          });
        } catch (err) {
          console.error('Error in SBP reduction 10% simulation:', err);
        }
      }

      // Simulation 7: Stop smoking (only if currently smoking)
      if (inputs.smoking === '1') {
        try {
          const modifiedInputs = { ...baseInputs, smoking: '0' };
          const result = await runSimulation(modifiedInputs, 'Stop Smoking');
          const changes = trackChanges(baseInputs, modifiedInputs);
          allSimulations.push({ 
            name: 'stop_smoking', 
            label: 'Stop Smoking', 
            result, 
            inputs: modifiedInputs, 
            changes,
            weight: currentWeight ? currentWeight.toFixed(2) : null,
            notes: []
          });
        } catch (err) {
          console.error('Error in stop smoking simulation:', err);
        }
      }

      // Simulation 8: All above + 5% reductions + stop smoking
      try {
        const modifiedInputs = { ...baseInputs };
        
        // Weight loss 5%
        if (inputs.height && inputs.bmi) {
          const weight = calculateWeightFromBMI(inputs.height, inputs.bmi);
          if (weight) {
            const newWeight = weight * 0.95;
            const newBMI = recalculateBMIFromWeight(newWeight, inputs.height);
            modifiedInputs.bmi = newBMI;
          }
        }
        
        // TC reduction 5%
        if (inputs.tc) {
          const tcValue = parseFloat(inputs.tc);
          modifiedInputs.tc = (tcValue * 0.95).toFixed(2);
        }
        
        // SBP reduction 5%
        let sbpNotes = [];
        if (inputs.sbp) {
          const sbpValue = parseFloat(inputs.sbp);
          const calculatedSBP = Math.round(sbpValue * 0.95);
          const clampedSBP = clampSBP(calculatedSBP);
          modifiedInputs.sbp = clampedSBP.value.toString();
          if (clampedSBP.note) {
            sbpNotes.push(clampedSBP.note);
          }
        }
        
        // Stop smoking
        if (inputs.smoking === '1') {
          modifiedInputs.smoking = '0';
        }
        
        const result = await runSimulation(modifiedInputs, 'All Improvements 5%');
        const changes = trackChanges(baseInputs, modifiedInputs);
        const weight = modifiedInputs.bmi && inputs.height ? calculateWeightFromBMI(inputs.height, modifiedInputs.bmi) : currentWeight;
        allSimulations.push({ 
          name: 'all_improvements_5', 
          label: 'All Improvements 5%', 
          result, 
          inputs: modifiedInputs, 
          changes,
          weight: weight ? parseFloat(weight).toFixed(2) : null,
          notes: sbpNotes
        });
      } catch (err) {
        console.error('Error in all improvements 5% simulation:', err);
      }

      // Simulation 9: All above + 10% reductions + stop smoking
      try {
        const modifiedInputs = { ...baseInputs };
        
        // Weight loss 10%
        if (inputs.height && inputs.bmi) {
          const weight = calculateWeightFromBMI(inputs.height, inputs.bmi);
          if (weight) {
            const newWeight = weight * 0.90;
            const newBMI = recalculateBMIFromWeight(newWeight, inputs.height);
            modifiedInputs.bmi = newBMI;
          }
        }
        
        // TC reduction 10%
        if (inputs.tc) {
          const tcValue = parseFloat(inputs.tc);
          modifiedInputs.tc = (tcValue * 0.90).toFixed(2);
        }
        
        // SBP reduction 10%
        let sbpNotes = [];
        if (inputs.sbp) {
          const sbpValue = parseFloat(inputs.sbp);
          const calculatedSBP = Math.round(sbpValue * 0.90);
          const clampedSBP = clampSBP(calculatedSBP);
          modifiedInputs.sbp = clampedSBP.value.toString();
          if (clampedSBP.note) {
            sbpNotes.push(clampedSBP.note);
          }
        }
        
        // Stop smoking
        if (inputs.smoking === '1') {
          modifiedInputs.smoking = '0';
        }
        
        const result = await runSimulation(modifiedInputs, 'All Improvements 10%');
        const changes = trackChanges(baseInputs, modifiedInputs);
        const weight = modifiedInputs.bmi && inputs.height ? calculateWeightFromBMI(inputs.height, modifiedInputs.bmi) : currentWeight;
        allSimulations.push({ 
          name: 'all_improvements_10', 
          label: 'All Improvements 10%', 
          result, 
          inputs: modifiedInputs, 
          changes,
          weight: weight ? parseFloat(weight).toFixed(2) : null,
          notes: sbpNotes
        });
      } catch (err) {
        console.error('Error in all improvements 10% simulation:', err);
      }

      setSimulations(allSimulations);
      setActiveTab('results');
      toast.success('Risk calculation and simulations completed successfully');
    } catch (err) {
      console.error('Error calling PREVENT API:', err);
      setError('Failed to calculate risk. Please check your inputs and try again.');
      toast.error('Failed to calculate risk');
    } finally {
      setLoading(false);
    }
  };

  // Render input field
  const renderInputField = (field, placeholder, type = 'text') => {
    const isRequired = !['uacr', 'hba1c', 'height'].includes(field);
    
    // Binary fields that should use dropdowns
    const binaryFields = ['sex', 'dm', 'smoking', 'bptreat', 'statin'];
    
    if (binaryFields.includes(field)) {
      const options = getBinaryFieldOptions(field);
      
      return (
        <IonItem className={validationErrors[field] ? 'ion-invalid' : ''}>
          <IonLabel position="stacked">
            {getFieldLabel(field)} {isRequired ? '*' : '(optional)'}
          </IonLabel>
          <IonSelect
            value={inputs[field]}
            onIonChange={e => handleInputChange(field, e.detail.value)}
            placeholder={`Select ${getFieldLabel(field).toLowerCase()}`}
            interface="popover"
          >
            {options.map(option => (
              <IonSelectOption key={option.value} value={option.value}>
                {option.label}
              </IonSelectOption>
            ))}
          </IonSelect>
          {validationErrors[field] && (
            <IonText color="danger" slot="helper">
              {validationErrors[field]}
            </IonText>
          )}
        </IonItem>
      );
    }
    
    return (
      <IonItem className={validationErrors[field] ? 'ion-invalid' : ''}>
        <IonLabel position="stacked">
          {getFieldLabel(field)} {isRequired ? '*' : '(optional)'}
        </IonLabel>
        <IonInput
          value={inputs[field]}
          onIonInput={e => handleInputChange(field, e.detail.value)}
          placeholder={placeholder}
          type={type}
          clearInput
        />
        {validationErrors[field] && (
          <IonText color="danger" slot="helper">
            {validationErrors[field]}
          </IonText>
        )}
      </IonItem>
    );
  };

  // Get options for binary fields
  const getBinaryFieldOptions = (field) => {
    const options = {
      sex: [
        { value: '0', label: 'Male' },
        { value: '1', label: 'Female' }
      ],
      dm: [
        { value: '0', label: 'No Diabetes' },
        { value: '1', label: 'Diabetes' }
      ],
      smoking: [
        { value: '0', label: 'Non-smoker' },
        { value: '1', label: 'Smoker' }
      ],
      bptreat: [
        { value: '0', label: 'No BP Treatment' },
        { value: '1', label: 'BP Treatment' }
      ],
      statin: [
        { value: '0', label: 'No Statin Use' },
        { value: '1', label: 'Statin Use' }
      ]
    };
    return options[field] || [];
  };

  // Handle tab change
  const handleTabChange = (e) => {
    setActiveTab(e.detail.value);
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Fetch member data and populate form
  const fetchMemberData = useCallback(async () => {
    if (!effectiveMemberId || !effectiveAuthToken) return;
    
    setLoadingMemberData(true);
    setError(null);
    
    try {
      // Fetch member details
      const memberResponse = await axios.get(
        `${config.apiUrl}/members_highlevel/${effectiveMemberId}`,
        {
          headers: {
            'Authorization': `Bearer ${effectiveAuthToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Fetch member metrics
      const metricsResponse = await axios.get(
        `${config.apiUrl}/metrics/${effectiveMemberId}`,
        {
          headers: {
            'Authorization': `Bearer ${effectiveAuthToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const memberData = memberResponse.data;
      const metrics = metricsResponse.data;
      
      // Process member data
      const newInputs = {
        sex: '',
        age: '',
        tc: '',
        hdl: '',
        sbp: '',
        dm: '',
        smoking: '',
        bmi: '',
        egfr: '',
        bptreat: '',
        statin: '',
        uacr: '',
        hba1c: '',
        height: ''
      };
      
      // Sex
      if (memberData.Sex) {
        newInputs.sex = memberData.Sex.toLowerCase() === 'male' ? '0' : '1';
      }
      
      // Age
      if (memberData.Date_of_Birth) {
        newInputs.age = calculateAge(memberData.Date_of_Birth).toString();
      }
      
      // Process metrics - find most recent by measurement_date
      const metricMap = {};
      metrics.forEach(metric => {
        const key = metric.name;
        if (!metricMap[key] || new Date(metric.measurement_date) > new Date(metricMap[key].measurement_date)) {
          metricMap[key] = metric;
        }
      });
      
      // Update data source date to most recent measurement
      const allDates = metrics.map(m => new Date(m.measurement_date));
      const mostRecentDate = new Date(Math.max(...allDates));
      setDataSourceDate(mostRecentDate.toLocaleDateString());
      
      // HDL Cholesterol
      if (metricMap['HDL Cholesterol']) {
        newInputs.hdl = metricMap['HDL Cholesterol'].value.toString();
      }
      
      // Total Cholesterol (value in mmol/L from metrics; UI expects mmol/L)
      if (metricMap['Total Cholesterol']) {
        newInputs.tc = metricMap['Total Cholesterol'].value.toString();
      }

      // Systolic Blood Pressure
      if (metricMap['Blood Pressure'] && metricMap['Blood Pressure'].bp_sys) {
        newInputs.sbp = metricMap['Blood Pressure'].bp_sys.toString();
      }
      
      // Diabetes
      if (metricMap['Diabetic']) {
        newInputs.dm = metricMap['Diabetic'].rating_text === 'Yes' ? '1' : '0';
      } else {
        newInputs.dm = '0'; // Default to No
      }
      
      // Smoking
      if (metricMap['Smoking']) {
        newInputs.smoking = metricMap['Smoking'].rating_text === 'Yes' ? '1' : '0';
      } else {
        newInputs.smoking = '0'; // Default to No
      }
      
      // BMI
      if (metricMap['BMI']) {
        newInputs.bmi = metricMap['BMI'].value.toString();
      }
      
      // Height - try direct metric first, then calculate from Weight and BMI
      if (metricMap['Height']) {
        newInputs.height = metricMap['Height'].value.toString();
      } else if (metricMap['Weight'] && metricMap['BMI']) {
        // Calculate height from Weight (kg) and BMI: height = sqrt(weight / BMI) * 100 (to convert m to cm)
        const weight = parseFloat(metricMap['Weight'].value);
        const bmi = parseFloat(metricMap['BMI'].value);
        if (weight > 0 && bmi > 0) {
          const heightInCm = Math.sqrt(weight / bmi) * 100;
          newInputs.height = heightInCm.toFixed(1);
        }
      }
      
      // eGFR
      if (metricMap['GFR']) {
        newInputs.egfr = metricMap['GFR'].value.toString();
      }
      
      // Blood Pressure Medication (BP treatment)
      if (metricMap['Blood Pressure Medication']) {
        newInputs.bptreat = metricMap['Blood Pressure Medication'].rating_text === 'Yes' ? '1' : '0';
      }

      // Statin Medication (statin use)
      if (metricMap['Statin Medication']) {
        newInputs.statin = metricMap['Statin Medication'].rating_text === 'Yes' ? '1' : '0';
      }

      // UACR (optional)
      if (metricMap['UACR']) {
        newInputs.uacr = metricMap['UACR'].value.toString();
      }
      
      // HbA1c (optional)
      if (metricMap['HbA1C']) {
        newInputs.hba1c = metricMap['HbA1C'].value.toString();
      }
      
      setInputs(newInputs);
      toast.success('Member data loaded successfully');
      
    } catch (err) {
      console.error('Error fetching member data:', err);
      setError('Failed to load member data. Please check your connection and try again.');
      toast.error('Failed to load member data');
    } finally {
      setLoadingMemberData(false);
    }
  }, [effectiveMemberId, effectiveAuthToken]);

  // Load member data when we have effective credentials
  useEffect(() => {
    if (effectiveMemberId && effectiveAuthToken) {
      fetchMemberData();
    }
  }, [effectiveMemberId, effectiveAuthToken, fetchMemberData]);

  // Check if age > 59 (30-year risk not available) and reset period if needed
  useEffect(() => {
    const age = Number(inputs.age);
    const is30YearAvailable = !isNaN(age) && age <= 59;
    if (!is30YearAvailable && riskPeriod === '30') {
      setRiskPeriod('10');
    }
  }, [inputs.age, riskPeriod]);


  // Helper function to convert mmol/L to mg/dL for cholesterol values
  const convertCholesterolToMgDl = (mmolLValue) => {
    if (!mmolLValue || mmolLValue.trim() === '') return '';
    const numericValue = parseFloat(mmolLValue);
    if (isNaN(numericValue)) return mmolLValue;
    return (numericValue * 38.6).toFixed(2);
  };

  // Helper function to convert mg/dL to mmol/L for cholesterol values
  const convertCholesterolToMmolL = (mgDlValue) => {
    if (!mgDlValue || mgDlValue === '') return '';
    const numericValue = parseFloat(mgDlValue);
    if (isNaN(numericValue)) return mgDlValue;
    return (numericValue / 38.6).toFixed(2);
  };

  // Helper function to convert UACR from mg/mmol to mg/g
  // Conversion factor: Creatinine molecular weight = 113.12 g/mol
  // 1 mmol creatinine = 0.11312 g creatinine
  // So: mg/mmol = mg per mmol = mg per 0.11312 g = (mg/0.11312) per g = mg/g * (1/0.11312)
  // Conversion: multiply by 8.84 (precisely: 1/0.11312 = 8.840...)
  const convertUACRToMgG = (mgMmolValue) => {
    if (!mgMmolValue || mgMmolValue.trim() === '') return '';
    const numericValue = parseFloat(mgMmolValue);
    if (isNaN(numericValue)) return mgMmolValue;
    // Conversion: mg/mmol to mg/g = multiply by 8.84
    return (numericValue * 8.84).toFixed(2);
  };

  // Helper function to convert UACR from mg/g to mg/mmol
  const convertUACRToMgMmol = (mgGValue) => {
    if (!mgGValue || mgGValue === '') return '';
    const numericValue = parseFloat(mgGValue);
    if (isNaN(numericValue)) return mgGValue;
    // Conversion: mg/g to mg/mmol = divide by 8.84
    return (numericValue / 8.84).toFixed(2);
  };

  // Calculate weight from BMI and height
  const calculateWeightFromBMI = (height, bmi) => {
    if (!height || !bmi) return null;
    const heightInM = parseFloat(height) / 100; // Convert cm to m
    const bmiValue = parseFloat(bmi);
    if (isNaN(heightInM) || isNaN(bmiValue) || heightInM <= 0) return null;
    return bmiValue * heightInM * heightInM; // weight in kg
  };

  // Recalculate BMI from weight and height
  const recalculateBMIFromWeight = (weight, height) => {
    if (!weight || !height) return null;
    const heightInM = parseFloat(height) / 100; // Convert cm to m
    const weightValue = parseFloat(weight);
    if (isNaN(heightInM) || isNaN(weightValue) || heightInM <= 0) return null;
    return (weightValue / (heightInM * heightInM)).toFixed(2);
  };

  // Call optimization endpoint to get AI-optimized inputs
  const callOptimizationEndpoint = async (baseInputs) => {
    try {
      // Prepare data in same format as prevent endpoint (cholesterol in mg/dL, UACR in mg/g)
      const optimizationData = {};
      
      // Add required parameters with conversions
      const requiredFields = ['sex', 'age', 'tc', 'hdl', 'sbp', 'dm', 'smoking', 'bmi', 'egfr', 'bptreat', 'statin'];
      requiredFields.forEach(field => {
        if (baseInputs[field]) {
          let value = baseInputs[field];
          // Convert cholesterol values from mmol/L to mg/dL
          if (field === 'tc' || field === 'hdl') {
            value = convertCholesterolToMgDl(baseInputs[field]);
          }
          optimizationData[field] = parseFloat(value);
        }
      });

      // Add optional parameters - send null if not provided
      if (baseInputs.uacr && baseInputs.uacr.trim() !== '') {
        optimizationData.uacr = parseFloat(convertUACRToMgG(baseInputs.uacr));
      } else {
        optimizationData.uacr = null;
      }
      if (baseInputs.hba1c && baseInputs.hba1c.trim() !== '') {
        optimizationData.hba1c = parseFloat(baseInputs.hba1c);
      } else {
        optimizationData.hba1c = null;
      }

      // Always add SDI
      optimizationData.sdi = 1.0;

      const response = await axios.post(
        'https://feedback-app-367524407367.europe-west2.run.app/prevent_optimisation',
        optimizationData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        return response.data;
      }
      throw new Error('Invalid response format from optimization endpoint');
    } catch (err) {
      console.error('Error calling optimization endpoint:', err);
      throw err;
    }
  };

  // Run a simulation with modified inputs
  const runSimulation = async (modifiedInputs, simulationName) => {
    try {
      const params = new URLSearchParams();
      
      // Add required parameters
      const requiredFields = ['sex', 'age', 'tc', 'hdl', 'sbp', 'dm', 'smoking', 'bmi', 'egfr', 'bptreat', 'statin'];
      requiredFields.forEach(field => {
        if (modifiedInputs[field]) {
          let value = modifiedInputs[field];
          // Convert cholesterol values from mmol/L to mg/dL
          if (field === 'tc' || field === 'hdl') {
            value = convertCholesterolToMgDl(modifiedInputs[field]);
          }
          params.append(field, value);
        }
      });

      // Add optional parameters - send "NA" if blank
      const optionalFields = ['uacr', 'hba1c'];
      optionalFields.forEach(field => {
        let value = modifiedInputs[field] && modifiedInputs[field].trim() !== '' ? modifiedInputs[field] : 'NA';
        // Convert UACR from mg/mmol to mg/g
        if (field === 'uacr' && value !== 'NA') {
          value = convertUACRToMgG(modifiedInputs[field]);
        }
        params.append(field, value);
      });

      // Always add SDI with default value of 10 - lower is better
      params.append('sdi', '1');

      const apiUrl = `https://plumber-api-367524407367.europe-west1.run.app/prevent_full?${params.toString()}`;
      
      const response = await axios.get(apiUrl);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }
      throw new Error('Invalid response format from API');
    } catch (err) {
      console.error(`Error running simulation ${simulationName}:`, err);
      throw err;
    }
  };

  // Clamp SBP to valid range (90-200 mmHg) and return note if clamped
  const clampSBP = (sbpValue) => {
    const clamped = Math.max(90, Math.min(200, sbpValue));
    const note = clamped !== sbpValue ? `Note: SBP clamped to ${clamped} mmHg (minimum allowed: 90 mmHg)` : null;
    return { value: clamped, note };
  };

  // Track which inputs changed in a simulation
  const trackChanges = (baseInputs, modifiedInputs) => {
    const changes = {};
    Object.keys(modifiedInputs).forEach(key => {
      if (baseInputs[key] !== modifiedInputs[key]) {
        changes[key] = {
          original: baseInputs[key],
          modified: modifiedInputs[key]
        };
      }
    });
    return changes;
  };

  // Render inputs tab
  const renderInputsTab = () => {
    return (
      <div className="tab-content">
        <IonCard className="header-card">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={calculatorOutline} />
              PREVENT Cardiovascular Risk Calculator
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonText>
              <p>Enter patient data to calculate cardiovascular risk using the PREVENT equations.</p>
              <p>Fields marked with * are required. Optional fields can be left empty.</p>
              {dataSourceDate && (
                <p><strong>Data Source Date:</strong> {dataSourceDate}</p>
              )}
            </IonText>
          </IonCardContent>
        </IonCard>

        {loadingMemberData && (
          <IonCard>
            <IonCardContent>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <IonSpinner name="crescent" />
                <IonText>Loading member data...</IonText>
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {error && (
          <IonCard color="danger">
            <IonCardContent>
              <IonText color="light">
                <IonIcon icon={warningOutline} />
                {error}
              </IonText>
            </IonCardContent>
          </IonCard>
        )}

        <IonCard className="input-form-card">
          <IonCardHeader>
            <IonCardTitle>Patient Information</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('sex')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('age', '30-79 years')}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('tc', '3.4-8.3 mmol/L')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('hdl', '0.5-2.6 mmol/L')}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('sbp', '90-200 mmHg')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('dm')}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('smoking')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('bmi', '18.5-39.9 kg/m²')}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('egfr', '>0 mL/min/1.73m²')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('bptreat')}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('statin')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('uacr', '≥0 mg/g (optional)')}
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12" size-md="6">
                  {renderInputField('hba1c', '>0% (optional)')}
                </IonCol>
                <IonCol size="12" size-md="6">
                  {renderInputField('height', '120-220 cm (optional)')}
                </IonCol>
              </IonRow>
            </IonGrid>

            <div className="submit-button-container">
              <IonButton 
                expand="block" 
                onClick={handleSubmit}
                disabled={loading}
                className="submit-button"
              >
                {loading ? (
                  <>
                    <IonSpinner name="crescent" />
                    Calculating Risk...
                  </>
                ) : (
                  <>
                    <IonIcon icon={calculatorOutline} slot="start" />
                    Calculate Risk
                  </>
                )}
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>
      </div>
    );
  };

  // Get chart data for filtered simulations
  const getChartData = () => {
    // Show ALL simulations on X-axis
    const labels = simulations.map(s => s.label);
    const periodSuffix = riskPeriod === '10' ? '10-Year' : '30-Year';
    const periodKey = riskPeriod === '10' ? '10yr' : '30yr';
    let datasetLabel = '';
    let data = [];
    if (riskType === 'cvd') {
      datasetLabel = `Total CVD Risk (${periodSuffix})`;
      data = simulations.map(s => s.result?.[`prevent_full_${periodKey}_CVD`] || 0);
    } else if (riskType === 'ascvd') {
      datasetLabel = `ASCVD Risk (${periodSuffix})`;
      data = simulations.map(s => s.result?.[`prevent_full_${periodKey}_ASCVD`] || 0);
    }
    
    // Create background colors array - use distinct color for AI Optimal
    const backgroundColor = simulations.map(s => {
      if (s.name === 'ai_optimal') {
        return 'rgba(75, 192, 192, 0.8)'; // teal/green for AI Optimal
      }
      return riskType === 'cvd' ? 'rgba(54, 162, 235, 0.8)' : 'rgba(255, 159, 64, 0.8)';
    });
    
    const borderColor = simulations.map(s => {
      if (s.name === 'ai_optimal') {
        return 'rgba(75, 192, 192, 1)';
      }
      return riskType === 'cvd' ? 'rgba(54, 162, 235, 1)' : 'rgba(255, 159, 64, 1)';
    });
    
    return {
      labels,
      datasets: [
        {
          label: datasetLabel,
          data,
          backgroundColor,
          borderColor,
          borderWidth: 1
        }
      ]
    };
  };

  // Get binary field display value
  const getBinaryFieldDisplay = (field, value) => {
    const options = getBinaryFieldOptions(field);
    const option = options.find(o => o.value === value);
    return option ? option.label : value;
  };

  // Render input comparison table
  const renderInputComparison = () => {
    const baselineSim = simulations.find(s => s.name === 'baseline');
    const selectedSim = simulations.find(s => s.name === selectedSimulation);
    
    if (!baselineSim || !selectedSim) return null;

    const baselineInputs = baselineSim.inputs;
    const selectedInputs = selectedSim.inputs;
    const changes = selectedSim.changes;
    const periodKey = riskPeriod === '10' ? '10yr' : '30yr';
    const periodSuffix = riskPeriod === '10' ? '10-Year' : '30-Year';

    const allFields = [
      'sex', 'age', 'tc', 'hdl', 'sbp', 'dm', 'smoking', 'bmi', 'egfr', 
      'bptreat', 'statin', 'uacr', 'hba1c', 'height'
    ];

    // Get risk values
    const baselineCVD = baselineSim.result?.[`prevent_full_${periodKey}_CVD`] || null;
    const baselineASCVD = baselineSim.result?.[`prevent_full_${periodKey}_ASCVD`] || null;
    const baselineHF = baselineSim.result?.[`prevent_full_${periodKey}_HF`] || null;
    
    const selectedCVD = selectedSim.result?.[`prevent_full_${periodKey}_CVD`] || null;
    const selectedASCVD = selectedSim.result?.[`prevent_full_${periodKey}_ASCVD`] || null;
    const selectedHF = selectedSim.result?.[`prevent_full_${periodKey}_HF`] || null;

    return (
      <IonCard className="input-comparison-card">
        <IonCardHeader>
          <IonCardTitle>Input Parameters & Risk Values Comparison</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <div className="input-comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Baseline</th>
                  <th>{selectedSim.label}</th>
                </tr>
              </thead>
              <tbody>
                {allFields.map(field => {
                  const isChanged = changes.hasOwnProperty(field);
                  const baselineValue = baselineInputs[field] || '-';
                  const selectedValue = selectedInputs[field] || '-';
                  
                  // Format display values
                  let baselineDisplay = baselineValue;
                  let selectedDisplay = selectedValue;
                  
                  if (['sex', 'dm', 'smoking', 'bptreat', 'statin'].includes(field)) {
                    baselineDisplay = baselineValue !== '-' ? getBinaryFieldDisplay(field, baselineValue) : '-';
                    selectedDisplay = selectedValue !== '-' ? getBinaryFieldDisplay(field, selectedValue) : '-';
                  }
                  
                  return (
                    <tr key={field} className={isChanged ? 'changed-row' : ''}>
                      <td className="field-label">{getFieldLabel(field)}</td>
                      <td className={isChanged ? 'original-value' : ''}>{baselineDisplay}</td>
                      <td className={isChanged ? 'modified-value' : ''}>{selectedDisplay}</td>
                    </tr>
                  );
                })}
                
                {/* Risk Values Section */}
                <tr className="risk-values-separator">
                  <td colSpan="3"><strong>Risk Values ({periodSuffix})</strong></td>
                </tr>
                
                <tr className="risk-value-row">
                  <td className="field-label">Total CVD Risk</td>
                  <td>{baselineCVD !== null ? `${baselineCVD.toFixed(1)}%` : 'N/A'}</td>
                  <td className={selectedCVD !== baselineCVD ? 'modified-value' : ''}>
                    {selectedCVD !== null ? `${selectedCVD.toFixed(1)}%` : 'N/A'}
                  </td>
                </tr>
                
                <tr className="risk-value-row">
                  <td className="field-label">ASCVD Risk</td>
                  <td>{baselineASCVD !== null ? `${baselineASCVD.toFixed(1)}%` : 'N/A'}</td>
                  <td className={selectedASCVD !== baselineASCVD ? 'modified-value' : ''}>
                    {selectedASCVD !== null ? `${selectedASCVD.toFixed(1)}%` : 'N/A'}
                  </td>
                </tr>
                
                <tr className="risk-value-row">
                  <td className="field-label">Heart Failure Risk</td>
                  <td>{baselineHF !== null ? `${baselineHF.toFixed(1)}%` : 'N/A'}</td>
                  <td className={selectedHF !== baselineHF ? 'modified-value' : ''}>
                    {selectedHF !== null ? `${selectedHF.toFixed(1)}%` : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>
            {selectedSim.weight && (
              <div className="weight-display">
                <strong>Calculated Weight:</strong> {selectedSim.weight} kg
              </div>
            )}
            {selectedSim.notes && selectedSim.notes.length > 0 && (
              <div className="notes-display">
                {selectedSim.notes.map((note, index) => (
                  <div key={index} className="note-item">
                    <IonIcon icon={informationCircleOutline} />
                    <span>{note}</span>
                  </div>
                ))}
              </div>
            )}
            {selectedSim.explanation && (
              <div className="explanation-display" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IonIcon icon={informationCircleOutline} />
                  <span>AI Optimization Explanation</span>
                </div>
                <div style={{ lineHeight: '1.6', color: '#333' }}>
                  {selectedSim.explanation}
                </div>
              </div>
            )}
            {(() => {
              const age = Number(inputs.age);
              const is30YearAvailable = !isNaN(age) && age <= 59;
              if (!is30YearAvailable) {
                return (
                  <div className="notes-display">
                    <div className="note-item">
                      <IonIcon icon={informationCircleOutline} />
                      <span>30-year risk projections are not available for ages over 59, as they extend beyond a reasonable lifespan.</span>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </IonCardContent>
      </IonCard>
    );
  };

  // Render results tab
  const renderResultsTab = () => {
    if (!results || simulations.length === 0) {
      return (
        <div className="tab-content">
          <IonCard>
            <IonCardContent>
              <IonText color="medium">
                <IonIcon icon={informationCircleOutline} />
                No results available. Please calculate risk first.
              </IonText>
            </IonCardContent>
          </IonCard>
        </div>
      );
    }

    // Check if age > 59 (30-year risk not available)
    const age = Number(inputs.age);
    const is30YearAvailable = !isNaN(age) && age <= 59;

    const chartData = getChartData();
    const periodSuffix = riskPeriod === '10' ? '10-Year' : '30-Year';

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `${periodSuffix} Cardiovascular Risk Comparison`,
          font: {
            size: 18
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`;
            }
          }
        },
        datalabels: {
          anchor: 'end',
          align: 'top',
          formatter: function(value) {
            return value.toFixed(1) + '%';
          },
          color: '#333',
          font: {
            weight: 'bold',
            size: 11
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Risk Percentage (%)'
          }
        }
      }
    };

    return (
      <div className="tab-content">
        <IonCard className="results-header-card">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={heartOutline} />
              Cardiovascular Risk Assessment Results & Simulations
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonText>
              <p>Based on the PREVENT equations, here are your cardiovascular risk predictions and simulation scenarios:</p>
            </IonText>
          </IonCardContent>
        </IonCard>

        <IonCard className="risk-period-card">
          <IonCardHeader>
            <IonCardTitle>Select Risk Period</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonSegment value={riskPeriod} onIonChange={(e) => setRiskPeriod(e.detail.value)} className="risk-period-segment">
              <IonSegmentButton value="10">
                <IonLabel>10-Year Risk</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="30" disabled={!is30YearAvailable || !simulations[0]?.result?.prevent_full_30yr_CVD}>
                <IonLabel>30-Year Risk</IonLabel>
              </IonSegmentButton>
            </IonSegment>
            {!is30YearAvailable && (
              <div className="risk-period-note">
                <IonIcon icon={informationCircleOutline} />
                <span>30-year risk projections are not available for ages over 59, as they extend beyond a reasonable lifespan.</span>
              </div>
            )}
          </IonCardContent>
        </IonCard>

        <IonCard className="risk-type-card">
          <IonCardHeader>
            <IonCardTitle>Risk Type</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonSegment value={riskType} onIonChange={e => setRiskType(e.detail.value)} className="risk-type-segment">
              <IonSegmentButton value="cvd">
                <IonLabel>CVD</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="ascvd">
                <IonLabel>ASCVD</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonCardContent>
        </IonCard>

        <IonCard className="chart-card">
          <IonCardHeader>
            <IonCardTitle>Risk Comparison Chart</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div className="chart-container">
              <Bar
                ref={chartRef}
                data={chartData}
                options={{
                  ...chartOptions,
                  onClick: (evt, elements) => handleChartBarClick(elements),
                }}
              />
            </div>
          </IonCardContent>
        </IonCard>

        {optimizationError && (
          <IonCard color="warning">
            <IonCardContent>
              <IonText color="dark">
                <IonIcon icon={warningOutline} />
                {optimizationError}
              </IonText>
            </IonCardContent>
          </IonCard>
        )}

        {(() => {
          const aiOptimalSim = simulations.find(s => s.name === 'ai_optimal');
          if (aiOptimalSim && aiOptimalSim.explanation) {
            return (
              <IonCard className="ai-explanation-card" style={{ marginTop: '20px' }}>
                <IonCardHeader>
                  <IonCardTitle>
                    <IonIcon icon={informationCircleOutline} />
                    AI Optimization Explanation
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonText>
                    <p style={{ lineHeight: '1.6', margin: 0 }}>
                      {aiOptimalSim.explanation}
                    </p>
                  </IonText>
                </IonCardContent>
              </IonCard>
            );
          }
          return null;
        })()}

        {renderInputComparison()}

        <div className="recalculate-button-container">
          <IonButton 
            expand="block" 
            fill="outline"
            onClick={() => setActiveTab('inputs')}
            className="recalculate-button"
          >
            <IonIcon icon={calculatorOutline} slot="start" />
            Modify Inputs & Recalculate
          </IonButton>
        </div>
      </div>
    );
  };

  const chartRef = React.useRef();

  const handleChartBarClick = (elements) => {
    if (!elements || !elements.length) return;
    const idx = elements[0]?.index;
    if (idx === undefined || idx < 0) return;
    const sim = simulations[idx];
    if (sim && sim.name) setSelectedSimulation(sim.name);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton fill="clear" slot="start" onClick={onBack || (() => history.goBack())}>
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <IonTitle>PREVENT Risk Calculator</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="prevent-calculator-container">
          <IonSegment value={activeTab} onIonChange={handleTabChange} className="tab-segment">
            <IonSegmentButton value="inputs">
              <IonIcon icon={calculatorOutline} />
              <IonLabel>Inputs</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="results">
              <IonIcon icon={heartOutline} />
              <IonLabel>Results</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {activeTab === 'inputs' && renderInputsTab()}
          {activeTab === 'results' && renderResultsTab()}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PreventCalculator;
